import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/reducers';
import { actions } from '../store/actions';
import { logService } from '../services/log.service';

export const useActivityMonitoring = () => {
    const dispatch = useDispatch();
    const { hasNewActivity, lastActivityCheck } = useSelector((state: RootState) => state.inspection);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const checkForNewActivities = async () => {
        try {
            // Get logs from the last 5 minutes to check for new activities
            const now = new Date();
            const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

            const response = await logService.getLogs({
                startDate: fiveMinutesAgo.toISOString(),
                endDate: now.toISOString(),
                page: 1,
            });

            if (response.logs && response.logs.length > 0) {
                // Check if there are any new logs since last check
                const latestLog = response.logs[0]; // Logs are sorted by newest first
                
                if (lastActivityCheck) {
                    const lastCheckTime = new Date(lastActivityCheck);
                    const latestLogTime = new Date(latestLog.createdAt);
                    
                    // If the latest log is newer than our last check, show notification
                    if (latestLogTime > lastCheckTime && !hasNewActivity) {
                        dispatch(actions.setHasNewActivity(true));
                    }
                } else {
                    // First time checking, set the timestamp but don't show notification
                    dispatch(actions.setLastActivityCheck(now.toISOString()));
                }
            }
        } catch (error) {
            console.error('Failed to check for new activities:', error);
        }
    };

    const startMonitoring = () => {
        // Check immediately
        checkForNewActivities();
        
        // Then check every 30 seconds
        intervalRef.current = setInterval(checkForNewActivities, 30000);
    };

    const stopMonitoring = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    useEffect(() => {
        startMonitoring();
        
        return () => {
            stopMonitoring();
        };
    }, []);

    return {
        hasNewActivity,
        checkForNewActivities,
        startMonitoring,
        stopMonitoring
    };
};
